import React, { useState, useEffect } from 'react'
import AppLayout from '../components/AppLayout'
import { useAuth } from '../contexts/AuthContext'
import { supabase, ugandaDate, ugandaMonth, ugandaDatetime } from '../lib/supabase'
import {
  predictAccrualExpenses,
  forecastWeeklyIncome,
  predictHarvestYield,
  scoreCustomerPaymentRisk,
  smartReorderQuantities,
  cropHealthTrends,
} from '../lib/mlEngine'

function MiniSparkline({ values, color = 'var(--green-mid)', height = 32 }) {
  if (!values || values.length < 2) return null
  const max = Math.max(...values)
  const min = Math.min(...values)
  const range = max - min || 1
  const w = 80, h = height
  const points = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w
    const y = h - ((v - min) / range) * h
    return `${x},${y}`
  }).join(' ')
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={points} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  )
}

function ConfidenceBadge({ level, color }) {
  return (
    <span style={{ fontSize: '0.65rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.1em', background: color + '22', color, padding: '2px 8px', borderRadius: 4, fontWeight: 700 }}>
      {level} confidence
    </span>
  )
}

function SectionHeader({ icon, title, subtitle }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--green-pale)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem' }}>{icon}</div>
      <div>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.1rem', fontWeight: 700, color: 'var(--green-deep)' }}>{title}</div>
        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{subtitle}</div>
      </div>
    </div>
  )
}

export default function MLInsights() {
  const { isAdmin } = useAuth()
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('overview')
  const [accrualPredictions, setAccrualPredictions] = useState([])
  const [incomeForecast, setIncomeForecast] = useState(null)
  const [harvestPredictions, setHarvestPredictions] = useState([])
  const [customerRisks, setCustomerRisks] = useState([])
  const [reorderSuggestions, setReorderSuggestions] = useState([])
  const [healthAlerts, setHealthAlerts] = useState([])
  const [lastUpdated, setLastUpdated] = useState(null)
  const [applying, setApplying] = useState(null)
  const [financialIntel, setFinancialIntel] = useState(null)

  useEffect(() => { loadAll() }, [])

  const loadAll = async () => {
    setLoading(true)
    try {
      const [accrual, income, harvest, customers, reorder, health] = await Promise.all([
        predictAccrualExpenses(),
        forecastWeeklyIncome(),
        predictHarvestYield(),
        scoreCustomerPaymentRisk(),
        smartReorderQuantities(),
        cropHealthTrends(),
      ])
      setAccrualPredictions(accrual)
      setIncomeForecast(income)
      setHarvestPredictions(harvest)
      setCustomerRisks(customers)
      setReorderSuggestions(reorder)
      setHealthAlerts(health)
      setLastUpdated(new Date())
    } catch (e) {
      console.error('ML load error:', e)
    }
    setLoading(false)
  }

  const applyAccrualPrediction = async (prediction) => {
    setApplying(prediction.category)
    const month = ugandaMonth()
    // Check if provision already exists for this month
    const { data: existing } = await supabase.from('accrual_provisions')
      .select('id').eq('category', prediction.category).eq('period_month', month).eq('is_reconciled', false)
    if (existing?.length) {
      alert(`A provision for ${prediction.category} already exists this month. Go to Weekly P&L → Accrual Provisions to view it.`)
      setApplying(null)
      return
    }
    await supabase.from('accrual_provisions').insert({
      period_month: month,
      category: prediction.category,
      description: `Auto-predicted from ${prediction.dataPoints} months of history`,
      estimated_amount: prediction.predicted,
    })
    alert(`✅ Provision of UGX ${prediction.predicted.toLocaleString()} created for ${prediction.category}. It will appear in your Weekly P&L automatically.`)
    setApplying(null)
  }

  const applyReorderQty = async (suggestion) => {
    setApplying(suggestion.name)
    const { data: item } = await supabase.from('stock_items').select('id').eq('name', suggestion.name).single()
    if (item) {
      await supabase.from('stock_items').update({ reorder_qty: suggestion.suggestedReorderQty }).eq('id', item.id)
      alert(`✅ Reorder quantity for ${suggestion.name} updated to ${suggestion.suggestedReorderQty} ${suggestion.unit}`)
    }
    setApplying(null)
    loadAll()
  }

  const totalAlerts = healthAlerts.length + customerRisks.filter(c => c.score < 50).length + reorderSuggestions.length

  if (loading) return (
    <AppLayout title="ML Insights">
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 60, gap: 16 }}>
        <span className="spinner dark" style={{ width: 32, height: 32 }} />
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.1rem', color: 'var(--text-muted)' }}>Learning from your data...</div>
      </div>
    </AppLayout>
  )

  return (
    <AppLayout title="ML Insights">
      <div className="section-header">
        <div className="section-icon">🧠</div>
        <div className="section-info">
          <h2>Smart Insights</h2>
          <p>Learns from your historical data to predict expenses, yields, and risks automatically.</p>
        </div>
        <button className="btn btn-secondary" style={{ marginLeft: 'auto' }} onClick={loadAll}>↻ Refresh</button>
      </div>

      {lastUpdated && (
        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: "'DM Mono',monospace", marginBottom: 16 }}>
          Last updated: {lastUpdated.toLocaleTimeString('en-UG')} · All predictions use your own historical data
        </div>
      )}

      {/* Summary KPIs */}
      <div className="kpi-grid" style={{ marginBottom: 20 }}>
        <div className="kpi">
          <div className="kpi-label">Expense Predictions</div>
          <div className="kpi-value">{accrualPredictions.length}</div>
          <div className="kpi-sub">categories learned</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Next Week Income</div>
          <div className="kpi-value" style={{ fontSize: '1.1rem' }}>{incomeForecast ? `UGX ${(incomeForecast.predictedIncome/1000000).toFixed(1)}M` : '—'}</div>
          <div className="kpi-sub">{incomeForecast?.trend || 'not enough data'}</div>
        </div>
        <div className="kpi" style={{ background: totalAlerts > 0 ? 'var(--red-pale)' : undefined }}>
          <div className="kpi-label">Active Alerts</div>
          <div className="kpi-value" style={{ color: totalAlerts > 0 ? 'var(--red)' : 'var(--green-mid)' }}>{totalAlerts}</div>
          <div className="kpi-sub">need attention</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">High Risk Customers</div>
          <div className="kpi-value" style={{ color: customerRisks.filter(c => c.score < 50).length > 0 ? 'var(--red)' : 'var(--green-mid)' }}>
            {customerRisks.filter(c => c.score < 50).length}
          </div>
          <div className="kpi-sub">of {customerRisks.length} customers</div>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>📊 Overview</button>
        <button className={`tab ${tab === 'expenses' ? 'active' : ''}`} onClick={() => setTab('expenses')}>💡 Expense Predictions</button>
        <button className={`tab ${tab === 'income' ? 'active' : ''}`} onClick={() => setTab('income')}>📈 Income Forecast</button>
        <button className={`tab ${tab === 'harvest' ? 'active' : ''}`} onClick={() => setTab('harvest')}>🌾 Yield Predictions</button>
        <button className={`tab ${tab === 'customers' ? 'active' : ''}`} onClick={() => setTab('customers')}>🤝 Customer Risk</button>
        <button className={`tab ${tab === 'stock' ? 'active' : ''}`} onClick={() => setTab('stock')}>📦 Smart Reorder</button>
        <button className={`tab ${tab === 'health' ? 'active' : ''}`} onClick={() => setTab('health')}>🌱 Crop Health</button>
        <button className={`tab ${tab === 'finance' ? 'active' : ''}`} onClick={() => setTab('finance')}>💰 Financial Intelligence</button>
      </div>

      {/* OVERVIEW */}
      {tab === 'overview' && (
        <div>
          {healthAlerts.length > 0 && (
            <div className="card" style={{ marginBottom: 16, border: '2px solid var(--red)' }}>
              <SectionHeader icon="🚨" title="Crop Health Alerts" subtitle="Deteriorating trends detected" />
              {healthAlerts.map(crop => crop.alerts.map((alert, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ background: alert.severity === 'High' ? 'var(--red-pale)' : 'var(--amber-pale)', color: alert.severity === 'High' ? 'var(--red)' : 'var(--amber)', borderRadius: 6, padding: '2px 8px', fontSize: '0.7rem', fontWeight: 700, whiteSpace: 'nowrap' }}>{alert.severity}</span>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{crop.cropName} — {alert.type}</div>
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>{alert.message}</div>
                  </div>
                </div>
              )))}
            </div>
          )}

          {accrualPredictions.length > 0 && (
            <div className="card" style={{ marginBottom: 16 }}>
              <SectionHeader icon="💡" title="This Month's Expense Predictions" subtitle="Click 'Apply' to auto-create provisions in your P&L" />
              {accrualPredictions.map(p => (
                <div key={p.category} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)', flexWrap: 'wrap', gap: 8 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <MiniSparkline values={p.history} color={p.confidenceColor} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{p.category}</div>
                      <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                        <ConfidenceBadge level={p.confidence} color={p.confidenceColor} />
                        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{p.trend} · {p.dataPoints} months data</span>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.2rem', fontWeight: 700, color: 'var(--green-deep)' }}>UGX {p.predicted.toLocaleString()}</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>range: {p.min.toLocaleString()} – {p.max.toLocaleString()}</div>
                    </div>
                    <button className="btn btn-secondary btn-sm" onClick={() => applyAccrualPrediction(p)} disabled={applying === p.category}>
                      {applying === p.category ? <span className="spinner" /> : '+ Apply'}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          {incomeForecast && (
            <div className="card" style={{ marginBottom: 16 }}>
              <SectionHeader icon="📈" title="Next Week Income Forecast" subtitle={`Based on ${incomeForecast.weeksOfData} weeks of data · ${incomeForecast.r2}% fit`} />
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12 }}>
                {[
                  ['Predicted Income', `UGX ${incomeForecast.predictedIncome.toLocaleString()}`, 'var(--green-mid)'],
                  ['Predicted Expenses', `UGX ${incomeForecast.predictedExpenses.toLocaleString()}`, 'var(--red)'],
                  ['Predicted Net', `${incomeForecast.predictedNet >= 0 ? '+' : ''}UGX ${incomeForecast.predictedNet.toLocaleString()}`, incomeForecast.predictedNet >= 0 ? 'var(--green-mid)' : 'var(--red)'],
                ].map(([label, val, color]) => (
                  <div key={label} style={{ background: 'var(--cream)', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.7rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 6 }}>{label}</div>
                    <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.2rem', fontWeight: 700, color }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 10, display: 'flex', gap: 16, fontSize: '0.78rem', color: 'var(--text-muted)', flexWrap: 'wrap' }}>
                <span>Best week ever: UGX {incomeForecast.bestWeek.toLocaleString()}</span>
                <span>Worst week: UGX {incomeForecast.worstWeek.toLocaleString()}</span>
                <span>Avg weekly net: UGX {incomeForecast.avgNet.toLocaleString()}</span>
                <span style={{ color: incomeForecast.trendColor, fontWeight: 700 }}>{incomeForecast.trend}</span>
              </div>
            </div>
          )}

          {customerRisks.filter(c => c.score < 50).length > 0 && (
            <div className="card">
              <SectionHeader icon="⚠️" title="High Risk Customers" subtitle="These customers have poor payment history" />
              {customerRisks.filter(c => c.score < 50).map(c => (
                <div key={c.name} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                  <div>
                    <div style={{ fontWeight: 700 }}>{c.name}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{c.recommendation}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, color: c.riskColor, fontSize: '1.1rem' }}>{c.score}/100</div>
                    <div style={{ fontSize: '0.7rem', color: c.riskColor }}>{c.riskLevel}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* EXPENSE PREDICTIONS */}
      {tab === 'expenses' && (
        <div className="card">
          <SectionHeader icon="💡" title="Accrual Expense Predictions" subtitle="The system learns from your reconciled bills to predict future expenses. The more months of data, the more accurate." />
          {accrualPredictions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">💡</div>
              <h3>Not enough data yet</h3>
              <p>Add accrual provisions in Weekly P&L and reconcile them when bills arrive. After 2–3 months the system will start predicting automatically.</p>
            </div>
          ) : accrualPredictions.map(p => (
            <div key={p.category} style={{ padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{p.category}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                    <ConfidenceBadge level={p.confidence} color={p.confidenceColor} />
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{p.trend}</span>
                    <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>{p.dataPoints} months of data</span>
                  </div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => applyAccrualPrediction(p)} disabled={applying === p.category}>
                  {applying === p.category ? <><span className="spinner" /> Applying...</> : '+ Apply to P&L'}
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 12 }}>
                {[
                  ['Predicted', `UGX ${p.predicted.toLocaleString()}`, p.confidenceColor],
                  ['Average', `UGX ${p.average.toLocaleString()}`, 'var(--text)'],
                  ['Lowest', `UGX ${p.min.toLocaleString()}`, 'var(--green-mid)'],
                  ['Highest', `UGX ${p.max.toLocaleString()}`, 'var(--red)'],
                ].map(([label, val, color]) => (
                  <div key={label} style={{ background: 'var(--cream)', borderRadius: 6, padding: '8px 10px' }}>
                    <div style={{ fontSize: '0.65rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 3 }}>{label}</div>
                    <div style={{ fontWeight: 700, color, fontSize: '0.9rem' }}>{val}</div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>History:</span>
                <MiniSparkline values={p.history} color={p.confidenceColor} height={28} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* INCOME FORECAST */}
      {tab === 'income' && (
        <div className="card">
          <SectionHeader icon="📈" title="Weekly Income Forecast" subtitle="Linear regression + seasonal pattern from your P&L history" />
          {!incomeForecast ? (
            <div className="empty-state"><div className="empty-icon">📈</div><h3>Not enough data yet</h3><p>Save at least 3 weekly P&L reports to enable income forecasting.</p></div>
          ) : (
            <>
              <div style={{ background: 'var(--green-pale)', borderRadius: 10, padding: 20, marginBottom: 20 }}>
                <div style={{ fontSize: '0.72rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.1em', color: 'var(--text-muted)', marginBottom: 8 }}>Next Week Forecast</div>
                <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '2rem', fontWeight: 700, color: 'var(--green-deep)' }}>UGX {incomeForecast.predictedIncome.toLocaleString()}</div>
                <div style={{ marginTop: 6, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <span style={{ color: incomeForecast.trendColor, fontWeight: 700, fontSize: '0.85rem' }}>{incomeForecast.trend}</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Model fit: {incomeForecast.r2}%</span>
                  <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Based on {incomeForecast.weeksOfData} weeks</span>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
                {[
                  ['📈 Predicted Income', `UGX ${incomeForecast.predictedIncome.toLocaleString()}`, 'var(--green-mid)'],
                  ['📉 Predicted Expenses', `UGX ${incomeForecast.predictedExpenses.toLocaleString()}`, 'var(--red)'],
                  ['💰 Predicted Net', `${incomeForecast.predictedNet >= 0 ? '+' : ''}UGX ${incomeForecast.predictedNet.toLocaleString()}`, incomeForecast.predictedNet >= 0 ? 'var(--green-mid)' : 'var(--red)'],
                  ['📊 Avg Weekly Net', `UGX ${incomeForecast.avgNet.toLocaleString()}`, 'var(--text)'],
                ].map(([label, val, color]) => (
                  <div key={label} style={{ background: 'var(--cream)', borderRadius: 8, padding: 14 }}>
                    <div style={{ fontSize: '0.78rem', marginBottom: 4, color: 'var(--text-muted)' }}>{label}</div>
                    <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.3rem', fontWeight: 700, color }}>{val}</div>
                  </div>
                ))}
              </div>
              <div className="alert alert-amber">
                ℹ️ Forecasts improve with more data. Best weeks: UGX {incomeForecast.bestWeek.toLocaleString()} · Worst: UGX {incomeForecast.worstWeek.toLocaleString()}. These are statistical predictions, not guarantees.
              </div>
            </>
          )}
        </div>
      )}

      {/* HARVEST YIELD */}
      {tab === 'harvest' && (
        <div className="card">
          <SectionHeader icon="🌾" title="Harvest Yield Predictions" subtitle="Learns from your harvest history to predict next yield per crop" />
          {harvestPredictions.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🌾</div><h3>Not enough harvest data</h3><p>Log at least 4 harvests per crop to enable yield predictions.</p></div>
          ) : harvestPredictions.map(h => (
            <div key={h.cropName} style={{ padding: '16px 0', borderBottom: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 12 }}>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '1rem' }}>{h.cropName}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 3 }}>
                    {h.dataPoints} harvests recorded · {h.trend}
                    {h.avgDaysBetweenHarvests && ` · avg every ${h.avgDaysBetweenHarvests} days`}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: '1.4rem', fontWeight: 700, color: 'var(--green-deep)' }}>{h.predictedYield} kg</div>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>predicted next harvest</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
                {[
                  ['Average Yield', `${h.averageYield} kg`, 'var(--text)'],
                  ['Best Harvest', `${h.bestHarvest} kg`, 'var(--green-mid)'],
                  ['Grade A %', `${h.gradeAPercent}%`, h.gradeAPercent > 70 ? 'var(--green-mid)' : 'var(--amber)'],
                ].map(([label, val, color]) => (
                  <div key={label} style={{ background: 'var(--cream)', borderRadius: 6, padding: '8px 10px' }}>
                    <div style={{ fontSize: '0.65rem', fontFamily: "'DM Mono',monospace", textTransform: 'uppercase', color: 'var(--text-muted)', marginBottom: 3 }}>{label}</div>
                    <div style={{ fontWeight: 700, color, fontSize: '0.9rem' }}>{val}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CUSTOMER RISK */}
      {tab === 'customers' && (
        <div className="card">
          <SectionHeader icon="🤝" title="Customer Payment Risk Scores" subtitle="Scored 0–100 based on payment history. Lower score = higher risk." />
          {customerRisks.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🤝</div><h3>No invoice history yet</h3><p>Customer scores appear once you have recorded invoices and payments.</p></div>
          ) : (
            <div className="table-wrap">
              <table>
                <thead><tr><th>Customer</th><th>Risk Score</th><th>Level</th><th>Invoices</th><th>Overdue</th><th>Avg Days to Pay</th><th>Recommendation</th></tr></thead>
                <tbody>
                  {customerRisks.map(c => (
                    <tr key={c.name}>
                      <td style={{ fontWeight: 600 }}>{c.name}</td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ width: 60, height: 6, background: 'var(--border)', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${c.score}%`, height: '100%', background: c.riskColor, borderRadius: 3 }} />
                          </div>
                          <span style={{ fontWeight: 700, color: c.riskColor, fontFamily: "'DM Mono',monospace" }}>{c.score}</span>
                        </div>
                      </td>
                      <td><span style={{ fontSize: '0.75rem', fontWeight: 700, color: c.riskColor }}>{c.riskLevel}</span></td>
                      <td style={{ fontSize: '0.78rem' }}>{c.totalInvoices}</td>
                      <td style={{ fontSize: '0.78rem', color: c.overdueCount > 0 ? 'var(--red)' : 'var(--text-muted)' }}>{c.overdueCount}</td>
                      <td style={{ fontSize: '0.78rem', fontFamily: "'DM Mono',monospace" }}>{c.avgDaysToPay !== null ? `${c.avgDaysToPay} days` : '—'}</td>
                      <td style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{c.recommendation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* SMART REORDER */}
      {tab === 'stock' && (
        <div className="card">
          <SectionHeader icon="📦" title="Smart Reorder Suggestions" subtitle="Analyses your actual usage rate to optimise reorder quantities" />
          {reorderSuggestions.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">📦</div><h3>All quantities look optimal</h3><p>The system will flag items when actual usage differs significantly from your reorder settings.</p></div>
          ) : reorderSuggestions.map(r => (
            <div key={r.name} style={{ padding: '14px 0', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10 }}>
              <div>
                <div style={{ fontWeight: 700 }}>{r.name}</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 3 }}>{r.suggestion}</div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>Weekly usage: {r.weeklyUsageRate} {r.unit} · Based on {r.dataPoints} stock movements</div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>Current: {r.currentReorderQty} {r.unit}</div>
                  <div style={{ fontWeight: 700, color: 'var(--green-deep)' }}>Suggested: {r.suggestedReorderQty} {r.unit}</div>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => applyReorderQty(r)} disabled={applying === r.name}>
                  {applying === r.name ? <span className="spinner" /> : '✓ Apply'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CROP HEALTH */}
      {tab === 'health' && (
        <div className="card">
          <SectionHeader icon="🌱" title="Crop Health Trend Analysis" subtitle="Detects deteriorating pH, EC, and plant health before they become problems" />
          {healthAlerts.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">🌱</div><h3>All crops looking stable</h3><p>No deteriorating trends detected. Keep logging daily readings — the more data, the better the early warnings.</p></div>
          ) : healthAlerts.map(crop => (
            <div key={crop.cropName} style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 10, color: 'var(--green-deep)' }}>{crop.cropName}</div>
              {crop.alerts.map((alert, i) => (
                <div key={i} style={{ display: 'flex', gap: 12, padding: '10px 14px', borderRadius: 8, background: alert.severity === 'High' ? 'var(--red-pale)' : 'var(--amber-pale)', border: `1px solid ${alert.severity === 'High' ? 'var(--red)' : 'var(--amber-light)'}`, marginBottom: 8 }}>
                  <span style={{ fontSize: '1rem' }}>{alert.severity === 'High' ? '🚨' : '⚠️'}</span>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '0.82rem', color: alert.severity === 'High' ? 'var(--red)' : 'var(--amber)', marginBottom: 3 }}>{alert.type} — {alert.severity} Priority</div>
                    <div style={{ fontSize: '0.8rem', color: 'var(--text)' }}>{alert.message}</div>
                  </div>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {tab === 'finance' && (
        <FinancialIntelligenceTab />
      )}
    </AppLayout>
  )
}

function FinancialIntelligenceTab() {
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => { loadIntel() }, [])

  const loadIntel = async () => {
    setLoading(true)
    const now = new Date()
    const startOf12Months = new Date(now)
    startOf12Months.setMonth(startOf12Months.getMonth() - 11)
    startOf12Months.setDate(1)
    const startStr = startOf12Months.toLocaleDateString('en-CA', { timeZone: 'Africa/Kampala' })

    const [jeRes, assetsRes] = await Promise.all([
      supabase.from('journal_entries').select('*').gte('entry_date', startStr).order('entry_date'),
      supabase.from('fixed_assets').select('*').eq('is_active', true),
    ])

    const je = jeRes.data || []
    const assets = assetsRes.data || []

    // Monthly breakdown
    const months = []
    for (let i = 11; i >= 0; i--) {
      const d = new Date(now)
      d.setMonth(d.getMonth() - i)
      const label = d.toLocaleDateString('en-UG', { month: 'short', year: '2-digit' })
      const ym = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`
      const mje = je.filter(e => e.entry_date?.startsWith(ym))
      const revenue = mje.filter(e => e.credit_account?.startsWith('4')).reduce((s,e) => s+e.amount, 0)
      const directCosts = mje.filter(e => e.debit_account?.startsWith('5')).reduce((s,e) => s+e.amount, 0)
      const opex = mje.filter(e => ['6','7','8'].includes(e.debit_account?.[0])).reduce((s,e) => s+e.amount, 0)
      const grossProfit = revenue - directCosts
      const netProfit = grossProfit - opex
      months.push({ label, ym, revenue, directCosts, opex, grossProfit, netProfit })
    }

    // Forecast: 3 months ahead
    const last3 = months.slice(-3)
    const avgRevenue = last3.reduce((s,m) => s+m.revenue, 0) / 3
    const avgCosts = last3.reduce((s,m) => s+m.directCosts, 0) / 3
    const avgOpex = last3.reduce((s,m) => s+m.opex, 0) / 3
    const revenueGrowth = last3.length > 1 && last3[0].revenue > 0
      ? (last3[2].revenue - last3[0].revenue) / last3[0].revenue : 0
    const forecast = [1,2,3].map(i => {
      const d = new Date(now); d.setMonth(d.getMonth() + i)
      const label = d.toLocaleDateString('en-UG', { month: 'long', year: 'numeric' })
      const projRevenue = Math.max(0, avgRevenue * (1 + revenueGrowth * 0.5 * i))
      const projCosts = avgCosts * 1.02
      const projNetProfit = projRevenue - projCosts - avgOpex
      return { label, projRevenue, projCosts, projOpex: avgOpex, projNetProfit }
    })

    // Input cost breakdown
    const inputCostByCategory = {}
    je.filter(e => e.debit_account?.startsWith('5')).forEach(e => {
      const key = e.description?.split('—')[0]?.trim() || e.debit_account
      inputCostByCategory[key] = (inputCostByCategory[key] || 0) + e.amount
    })

    // Seasonal pattern
    const seasonalPattern = {}
    je.filter(e => e.credit_account?.startsWith('4')).forEach(e => {
      const month = new Date(e.entry_date).getMonth()
      const mName = new Date(2024, month).toLocaleDateString('en-UG', { month: 'short' })
      seasonalPattern[mName] = (seasonalPattern[mName] || 0) + e.amount
    })
    const maxSeason = Math.max(...Object.values(seasonalPattern), 1)
    const seasonalInsight = Object.entries(seasonalPattern).sort(([,a],[,b]) => b-a)

    // Asset health
    const assetAlerts = assets.map(asset => {
      const alerts = []
      if (asset.commissioning_date && asset.useful_life_years) {
        const ageYears = (now - new Date(asset.commissioning_date)) / (365.25*24*3600*1000)
        const pctUsed = (ageYears / asset.useful_life_years) * 100
        const remainingYears = asset.useful_life_years - ageYears
        if (pctUsed > 80) alerts.push({ level:'high', msg:`${Math.round(pctUsed)}% of useful life used — plan replacement within ${Math.round(remainingYears*12)} months` })
        else if (pctUsed > 60) alerts.push({ level:'medium', msg:`${Math.round(pctUsed)}% of useful life used — budget for maintenance` })
      }
      const maintenanceCost = je.filter(e => e.debit_account==='6400' && e.description?.toLowerCase().includes((asset.name||'').toLowerCase())).reduce((s,e)=>s+e.amount,0)
      if (maintenanceCost > (asset.total_cost||0)*0.1) alerts.push({ level:'medium', msg:`Maintenance UGX ${maintenanceCost.toLocaleString()} is ${Math.round(maintenanceCost/(asset.total_cost||1)*100)}% of asset value` })
      return { name: asset.name, category: asset.category, totalCost: asset.total_cost, alerts }
    }).filter(a => a.alerts.length > 0)

    // Cash metrics
    const cashJe = je.filter(e => e.debit_account==='1101' || e.credit_account==='1101')
    const totalCashIn = cashJe.filter(e=>e.debit_account==='1101').reduce((s,e)=>s+e.amount,0)
    const totalCashOut = cashJe.filter(e=>e.credit_account==='1101').reduce((s,e)=>s+e.amount,0)
    const currentCash = totalCashIn - totalCashOut
    const last3Opex = months.slice(-3).reduce((s,m)=>s+m.opex+m.directCosts,0)/3
    const monthsOfRunway = last3Opex > 0 ? currentCash/last3Opex : null
    const recentWithRevenue = months.filter(m=>m.revenue>0).slice(-6)
    const avgGrossMargin = recentWithRevenue.length > 0
      ? recentWithRevenue.reduce((s,m)=>s+(m.grossProfit/(m.revenue||1)*100),0)/recentWithRevenue.length : 0

    setData({ months, forecast, inputCostByCategory, seasonalInsight, maxSeason, assetAlerts, currentCash, monthsOfRunway, avgGrossMargin, last3Opex, avgRevenue })
    setLoading(false)
  }

  const fmt = (n) => Math.round(n||0).toLocaleString()

  if (loading) return <div className="empty-state"><p>Analysing your financial data...</p></div>
  if (!data) return null

  const { months, forecast, inputCostByCategory, seasonalInsight, maxSeason, assetAlerts, currentCash, monthsOfRunway, avgGrossMargin, last3Opex, avgRevenue } = data

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:20 }}>

      {/* Cash Runway */}
      <div className="card" style={{ borderLeft:`4px solid ${monthsOfRunway!==null&&monthsOfRunway<3?'var(--red)':monthsOfRunway!==null&&monthsOfRunway<6?'var(--amber)':'var(--green-bright)'}` }}>
        <div className="card-title" style={{ marginBottom:14 }}>🏦 Financial Health Snapshot</div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))', gap:16 }}>
          {[
            ['Cash at Bank', `UGX ${fmt(currentCash)}`, currentCash>0?'var(--green-deep)':'var(--red)'],
            ['Monthly Burn Rate', `UGX ${fmt(last3Opex)}`, 'var(--text)'],
            ['Cash Runway', monthsOfRunway!==null?`${monthsOfRunway.toFixed(1)} months`:'—', monthsOfRunway!==null&&monthsOfRunway<3?'var(--red)':monthsOfRunway!==null&&monthsOfRunway<6?'var(--amber)':'var(--green-deep)'],
            ['Avg Gross Margin', `${avgGrossMargin.toFixed(1)}%`, avgGrossMargin>40?'var(--green-deep)':avgGrossMargin>20?'var(--amber)':'var(--red)'],
            ['Avg Monthly Revenue', `UGX ${fmt(avgRevenue)}`, 'var(--green-mid)'],
          ].map(([label,value,color]) => (
            <div key={label}>
              <div style={{ fontSize:'0.7rem', color:'var(--text-muted)', textTransform:'uppercase', fontFamily:"'DM Mono',monospace", marginBottom:4 }}>{label}</div>
              <div style={{ fontWeight:800, fontSize:'1rem', color }}>{value}</div>
            </div>
          ))}
        </div>
        {monthsOfRunway!==null && monthsOfRunway<6 && (
          <div style={{ marginTop:12, padding:'8px 12px', background:monthsOfRunway<3?'var(--red-pale)':'var(--amber-pale)', borderRadius:6, fontSize:'0.78rem', color:monthsOfRunway<3?'var(--red)':'var(--amber)' }}>
            {monthsOfRunway<3 ? `⚠️ Critical: Cash runs out in ${monthsOfRunway.toFixed(1)} months at current burn rate. Accelerate collections or reduce costs immediately.`
              : `⚡ Monitor: ${monthsOfRunway.toFixed(1)} months of runway. Build a cash buffer before it drops below 3 months.`}
          </div>
        )}
      </div>

      {/* 3-Month Forecast */}
      <div className="card">
        <div className="card-title">📈 3-Month Profitability Forecast</div>
        <div className="card-subtitle">Based on your last 3 months' trend. Assumes 50% continuation of recent growth rate.</div>
        {avgRevenue === 0 ? (
          <div className="alert alert-amber" style={{ marginTop:12, fontSize:'0.78rem' }}>Not enough revenue history yet. Record sales invoices and the forecast will populate automatically.</div>
        ) : (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginTop:14 }}>
            {forecast.map((m,i) => (
              <div key={i} style={{ background:m.projNetProfit>=0?'var(--green-pale)':'var(--red-pale)', borderRadius:8, padding:'14px 16px' }}>
                <div style={{ fontWeight:700, fontSize:'0.82rem', marginBottom:10, color:'var(--green-deep)' }}>{m.label}</div>
                {[['Projected Revenue',m.projRevenue,'var(--green-deep)'],['Est. Direct Costs',m.projCosts,'var(--text-muted)'],['Est. Opex',m.projOpex,'var(--text-muted)']].map(([label,val,color]) => (
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.74rem', marginBottom:4 }}>
                    <span style={{ color:'var(--text-muted)' }}>{label}</span>
                    <span style={{ fontFamily:"'DM Mono',monospace", color }}>{fmt(val)}</span>
                  </div>
                ))}
                <div style={{ borderTop:'1px solid var(--border)', marginTop:8, paddingTop:8, display:'flex', justifyContent:'space-between', fontWeight:800 }}>
                  <span style={{ fontSize:'0.82rem' }}>Net Profit</span>
                  <span style={{ fontFamily:"'DM Mono',monospace", color:m.projNetProfit>=0?'var(--green-deep)':'var(--red)' }}>
                    {m.projNetProfit<0?'(':''}{ fmt(Math.abs(m.projNetProfit))}{m.projNetProfit<0?')':''}
                  </span>
                </div>
                <div style={{ fontSize:'0.65rem', color:'var(--text-muted)', marginTop:4, textAlign:'right' }}>
                  {m.projNetProfit>=0?'✅ Profitable':'⚠️ Loss projected'}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 12-Month P&L Trend */}
      <div className="card">
        <div className="card-title">📊 12-Month Revenue vs Cost Trend</div>
        <div className="table-wrap">
          <table>
            <thead><tr>
              <th>Month</th>
              <th style={{ textAlign:'right' }}>Revenue</th>
              <th style={{ textAlign:'right' }}>Direct Costs</th>
              <th style={{ textAlign:'right' }}>Opex</th>
              <th style={{ textAlign:'right' }}>Gross Profit</th>
              <th style={{ textAlign:'right' }}>Net P&L</th>
              <th style={{ textAlign:'right' }}>GM%</th>
            </tr></thead>
            <tbody>
              {months.map((m,i) => (
                <tr key={i} style={{ background:m.netProfit<0&&m.revenue>0?'var(--red-pale)':'transparent' }}>
                  <td style={{ fontFamily:"'DM Mono',monospace", fontSize:'0.78rem', fontWeight:600 }}>{m.label}</td>
                  <td style={{ textAlign:'right', fontFamily:"'DM Mono',monospace", fontSize:'0.78rem', color:'var(--green-deep)' }}>{m.revenue>0?fmt(m.revenue):'—'}</td>
                  <td style={{ textAlign:'right', fontFamily:"'DM Mono',monospace", fontSize:'0.78rem', color:'var(--text-muted)' }}>{m.directCosts>0?fmt(m.directCosts):'—'}</td>
                  <td style={{ textAlign:'right', fontFamily:"'DM Mono',monospace", fontSize:'0.78rem', color:'var(--text-muted)' }}>{m.opex>0?fmt(m.opex):'—'}</td>
                  <td style={{ textAlign:'right', fontFamily:"'DM Mono',monospace", fontSize:'0.78rem', fontWeight:600, color:m.grossProfit>=0?'var(--green-mid)':'var(--red)' }}>
                    {m.revenue>0?fmt(m.grossProfit):'—'}
                  </td>
                  <td style={{ textAlign:'right', fontFamily:"'DM Mono',monospace", fontSize:'0.78rem', fontWeight:700, color:m.netProfit>=0?'var(--green-deep)':'var(--red)' }}>
                    {(m.revenue>0||m.directCosts>0||m.opex>0)?(m.netProfit<0?`(${fmt(Math.abs(m.netProfit))})`:fmt(m.netProfit)):'—'}
                  </td>
                  <td style={{ textAlign:'right', fontFamily:"'DM Mono',monospace", fontSize:'0.75rem' }}>
                    {m.revenue>0?`${(m.grossProfit/m.revenue*100).toFixed(1)}%`:'—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Seasonal Pattern */}
      {seasonalInsight.length>0 && (
        <div className="card">
          <div className="card-title">🌦 Seasonal Revenue Pattern</div>
          <div className="card-subtitle">Use this to plan production cycles and cash buffers. Start highest-yield cycles 4–6 weeks before your peak month.</div>
          <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:8 }}>
            {seasonalInsight.slice(0,12).map(([month,amount],i) => (
              <div key={month} style={{ display:'flex', alignItems:'center', gap:12 }}>
                <div style={{ width:36, fontSize:'0.78rem', fontFamily:"'DM Mono',monospace", color:'var(--text-muted)' }}>{month}</div>
                <div style={{ flex:1, background:'var(--cream)', borderRadius:4, overflow:'hidden', height:18 }}>
                  <div style={{ width:`${(amount/maxSeason)*100}%`, height:'100%', background:i===0?'var(--green-deep)':i===seasonalInsight.length-1?'var(--red)':'var(--green-bright)', borderRadius:4 }} />
                </div>
                <div style={{ width:100, textAlign:'right', fontFamily:"'DM Mono',monospace", fontSize:'0.75rem', fontWeight:600 }}>UGX {fmt(amount)}</div>
                {i===0 && <span className="badge badge-green" style={{ fontSize:'0.6rem', minWidth:36 }}>Peak</span>}
                {i===seasonalInsight.length-1&&seasonalInsight.length>1 && <span className="badge badge-grey" style={{ fontSize:'0.6rem', minWidth:36 }}>Low</span>}
                {i>0&&i<seasonalInsight.length-1 && <span style={{ minWidth:36 }}></span>}
              </div>
            ))}
          </div>
          {seasonalInsight.length>1 && (
            <div style={{ marginTop:12, padding:'10px 12px', background:'var(--green-pale)', borderRadius:6, fontSize:'0.78rem' }}>
              💡 Your peak revenue month is <strong>{seasonalInsight[0][0]}</strong>. Your lowest is <strong>{seasonalInsight[seasonalInsight.length-1][0]}</strong>. Build 2–3 months of cash reserves during peak to cover the slow period.
            </div>
          )}
        </div>
      )}

      {/* Input Cost Analysis */}
      {Object.keys(inputCostByCategory).length>0 && (
        <div className="card">
          <div className="card-title">🧪 Input Cost Breakdown (12 months)</div>
          <div className="card-subtitle">Where your direct production costs are going. High-cost items worth negotiating or substituting.</div>
          <div style={{ marginTop:14, display:'flex', flexDirection:'column', gap:8 }}>
            {Object.entries(inputCostByCategory).sort(([,a],[,b])=>b-a).map(([cat,amt],i) => {
              const total = Object.values(inputCostByCategory).reduce((s,v)=>s+v,0)
              const pct = total>0?(amt/total*100).toFixed(1):0
              return (
                <div key={cat} style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ width:140, fontSize:'0.75rem', color:'var(--text)', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{cat}</div>
                  <div style={{ flex:1, background:'var(--cream)', borderRadius:4, overflow:'hidden', height:16 }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:i===0?'var(--amber)':'var(--green-bright)', borderRadius:4 }} />
                  </div>
                  <div style={{ width:40, textAlign:'right', fontSize:'0.72rem', fontFamily:"'DM Mono',monospace", color:'var(--text-muted)' }}>{pct}%</div>
                  <div style={{ width:110, textAlign:'right', fontFamily:"'DM Mono',monospace", fontSize:'0.75rem', fontWeight:600 }}>UGX {fmt(amt)}</div>
                </div>
              )
            })}
          </div>
          {Object.entries(inputCostByCategory).sort(([,a],[,b])=>b-a)[0] && (() => {
            const [topCat, topAmt] = Object.entries(inputCostByCategory).sort(([,a],[,b])=>b-a)[0]
            return (
              <div style={{ marginTop:12, padding:'10px 12px', background:'var(--amber-pale)', borderRadius:6, fontSize:'0.78rem' }}>
                💡 <strong>{topCat}</strong> is your largest input cost. A 10% reduction saves UGX {fmt(topAmt*0.1)} per year — worth negotiating supplier terms or exploring substitutes.
              </div>
            )
          })()}
        </div>
      )}

      {/* Fixed Asset Health */}
      <div className="card">
        <div className="card-title">🏗 Fixed Asset Health & Replacement Planning</div>
        <div className="card-subtitle">Assets approaching end of useful life or with high maintenance costs relative to value.</div>
        {assetAlerts.length===0 ? (
          <div style={{ padding:'20px 0', textAlign:'center', color:'var(--text-muted)', fontSize:'0.82rem' }}>✅ All fixed assets are within healthy range.</div>
        ) : assetAlerts.map(asset => (
          <div key={asset.name} style={{ marginBottom:12, padding:'12px 16px', borderRadius:8, background:'var(--cream)', border:'1px solid var(--border)' }}>
            <div style={{ fontWeight:700, fontSize:'0.85rem', marginBottom:8 }}>
              {asset.name}
              <span style={{ marginLeft:8, fontSize:'0.72rem', color:'var(--text-muted)', fontWeight:400 }}>{asset.category} · UGX {fmt(asset.totalCost)}</span>
            </div>
            {asset.alerts.map((alert,i) => (
              <div key={i} style={{ display:'flex', gap:8, alignItems:'flex-start', marginBottom:4 }}>
                <span>{alert.level==='high'?'🔴':'🟡'}</span>
                <span style={{ fontSize:'0.78rem', color:alert.level==='high'?'var(--red)':'var(--amber)' }}>{alert.msg}</span>
              </div>
            ))}
          </div>
        ))}
        <div style={{ marginTop:12, padding:'10px 12px', background:'var(--green-pale)', borderRadius:6, fontSize:'0.78rem' }}>
          💡 Budget 2–5% of total asset value per year for maintenance. For assets over 80% of useful life, start setting aside replacement reserves in a dedicated savings account.
        </div>
      </div>

    </div>
  )
}

